import selectors from '../../../rootSelectors';
import { ONE_SECOND, TRIGGER_CLICK_DURATION, GENERAL_AUDIO_DELAY } from '../../../constants/time';
import {
    animatePointerPosition,
    animatePointerClick,
    togglePointerVisibility,
    blockScreen,
    unblockScreen,
    activateNextButton,
    greyOutScreen,
    deactivateNextButton,
    setNextButtonClickable,
} from '../../../common/screen/screenActions';
import {
    highlightPossibleResponse,
    resetSelectedIndex,
    resetQuestionsAndTimeTrackingState,
    resetSelectedMultiChoiceIndexes,
    selectMultipleResponses,
} from '../../../common/questions/questionsActions';
import {
    selectPossibleResponses,
    setEnabledResponses,
    setButtonGridEnabled,
    setReadyScriptPlayed,
    setClearButtonEnabled,
} from '../../spellingActions';
import { playAvatarAudio, setAvatarInstructions } from '../../../common/avatar/avatarActions';
import { playStimulusAudio } from '../../../common/stimulus/stimulusButtonActions';
import { goToCountdown } from '../../../common/navigation/navigationActions';
import { PRACTICE } from '../../../constants/assessmentItemType';
import { SPELLING } from '../../../constants/measure';
import POSITION, { letterPosition } from '../../../constants/pointerCursor';
import {
    PRACTICE_LETTERS,
    PRACTICE_LETTERS_SPANISH,
    SPELLING_AUDIO_INSTRUCTIONS,
    CLEAR_BUTTON_INSTRUCTION_RESPONSE,
    CLEAR_BUTTON_INSTRUCTION_RESPONSE_SPANISH,
} from '../../spellingConstants';
import { LANGUAGES } from '../../../common/localization';

const animateClickOnLetter = letter => async dispatch => {
    await dispatch(animatePointerPosition(letterPosition(letter), ONE_SECOND));
    dispatch(highlightPossibleResponse(letter));
    dispatch(selectPossibleResponses(letter));
    return dispatch(animatePointerClick(TRIGGER_CLICK_DURATION));
};

const spellPracticeWord = () => (dispatch, getStore) => {
    const state = getStore();
    const language = selectors.getCurrentLanguage(state);
    const practiceLetters =
        language === LANGUAGES.SPANISH ? PRACTICE_LETTERS_SPANISH : PRACTICE_LETTERS;

    return practiceLetters.reduce(
        (promise, letter, index) =>
            promise
                .then(() => dispatch(animateClickOnLetter(letter)))
                .then(() => {
                    if (index === 0) dispatch(activateNextButton());
                }),
        Promise.resolve(),
    );
};

const animateClickOnClear = () => async dispatch => {
    await dispatch(animatePointerPosition(POSITION.CLEAR_POSITION, ONE_SECOND));
    await dispatch(animatePointerClick(TRIGGER_CLICK_DURATION));
    dispatch(resetSelectedMultiChoiceIndexes());
    await dispatch(spellPracticeWord());
    await dispatch(animatePointerPosition(POSITION.OK_POSITION, ONE_SECOND));
    return dispatch(animatePointerClick(TRIGGER_CLICK_DURATION));
};

const playPracticeActiveScript = stimulusAudio => async (dispatch, getStore) => {
    const state = getStore();
    const language = selectors.getCurrentLanguage(state);

    const correctResponse = selectors.getSpellingCorrectResponse(state, PRACTICE);
    await dispatch(spellPracticeWord());
    await dispatch(animatePointerPosition(POSITION.OK_POSITION));
    dispatch(resetSelectedIndex());
    await dispatch(playAvatarAudio(SPELLING_AUDIO_INSTRUCTIONS[language].clickOKInstruction));
    await dispatch(animatePointerClick(TRIGGER_CLICK_DURATION));
    dispatch(resetSelectedMultiChoiceIndexes());
    dispatch(deactivateNextButton());
    dispatch(togglePointerVisibility());
    await dispatch(
        playAvatarAudio(SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionActive),
    );
    dispatch(setAvatarInstructions(SPELLING_AUDIO_INSTRUCTIONS[language].replayInstruction));
    await dispatch(playStimulusAudio([stimulusAudio], GENERAL_AUDIO_DELAY));
    dispatch(setEnabledResponses([correctResponse[0]]));
    dispatch(setButtonGridEnabled(false));
    dispatch(setClearButtonEnabled(false));
    return Promise.resolve();
};

export const playPracticeReadyScript = () => async (dispatch, getState) => {
    const state = getState();
    const language = selectors.getCurrentLanguage(state);

    dispatch(blockScreen());
    dispatch(greyOutScreen());
    await dispatch(playAvatarAudio(SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionReady));
    dispatch(setAvatarInstructions(SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionReady));
    dispatch(setReadyScriptPlayed());
    dispatch(unblockScreen());
};

export const playClearButtonInstructions = () => async (dispatch, getStore) => {
    const state = getStore();
    const language = selectors.getCurrentLanguage(state);

    dispatch(blockScreen());
    dispatch(deactivateNextButton());
    dispatch(togglePointerVisibility());
    dispatch(
        selectMultipleResponses(
            language === LANGUAGES.SPANISH
                ? CLEAR_BUTTON_INSTRUCTION_RESPONSE_SPANISH
                : CLEAR_BUTTON_INSTRUCTION_RESPONSE,
        ),
    );
    await dispatch(
        playAvatarAudio(SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionClearButton),
    );
    dispatch(
        setAvatarInstructions(SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionClearButton),
    );
    await dispatch(animateClickOnClear());
    dispatch(togglePointerVisibility());
    return Promise.resolve();
};

export const narratePracticeScreen = stimulusAudio => async (dispatch, getState) => {
    const state = getState();
    const language = selectors.getCurrentLanguage(state);

    const instructionAudio = [
        SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstruction,
        SPELLING_AUDIO_INSTRUCTIONS[language].practiceInstructionStart,
    ];

    await dispatch(playAvatarAudio(instructionAudio));
    await dispatch(playStimulusAudio([stimulusAudio], GENERAL_AUDIO_DELAY));
    return dispatch(playPracticeActiveScript(stimulusAudio));
};

export const onResponseSelect = letter => async (dispatch, getStore) => {
    const state = getStore();
    const language = selectors.getCurrentLanguage(state);

    const correctResponse = selectors.getSpellingCorrectResponse(state, PRACTICE);
    const letterIndex = correctResponse.indexOf(letter);
    const nextResponse = correctResponse[letterIndex + 1];

    if (letterIndex === 0) {
        dispatch(activateNextButton());
        dispatch(setNextButtonClickable(false));
    }

    if (letterIndex >= 0 && nextResponse) {
        return dispatch(setEnabledResponses([nextResponse]));
    }

    dispatch(setEnabledResponses());
    dispatch(blockScreen());

    await dispatch(playAvatarAudio(SPELLING_AUDIO_INSTRUCTIONS[language].clickOk));
    dispatch(setAvatarInstructions(SPELLING_AUDIO_INSTRUCTIONS[language].clickOk));
    dispatch(setNextButtonClickable(true));
    dispatch(unblockScreen());
    return Promise.resolve();
};

export const onNext = () => async (dispatch, getStore) => {
    const state = getStore();
    const isSpellingReadyScriptPlayed = selectors.getIsSpellingReadyScriptPlayed(state);

    if (isSpellingReadyScriptPlayed) {
        dispatch(goToCountdown(SPELLING.ASSESSMENT, null));
    }

    dispatch(resetQuestionsAndTimeTrackingState());
    dispatch(setButtonGridEnabled(true));
    dispatch(setClearButtonEnabled(true));
    await dispatch(playClearButtonInstructions());
    return dispatch(playPracticeReadyScript());
};
